import { Suspense, useEffect, useState } from 'react';
import {
  useRoutes, useNavigate, useLocation, Navigate as Redirect
} from 'react-router-dom';
import Navbar from './header/Navbar';
import HeadBanner from './header/HeadBanner';
import DynamicPageRenderer from './core/DynamicPageRenderer';
import TestFlightPage from '../pages/TestFlightPage';
import OcaPage from '../pages/OcaPage';
import LogoutPage from '../pages/LogoutPage';
import MaintenancePage from '../pages/MaintenancePage';
import AnyErrorBoundary from './global/AnyErrorBoundary';
import Loading from './global/Loading';
import useShallowEqualSelector from './core/useShallowEqualSelector';
import useTheme from '../global-hooks/useTheme';
import storageAndCookie from '../global-utils/storage-and-cookies';
import ComponentsArrRenderer from './core/ComponentsArrRenderer';
import PageRenderer from './core/PageRenderer';
import { vmRefProcessorApp } from '../global-utils';
import { setVmFunctionNavigate } from '../global-utils/vmFunctions/navigate';
import { AccessControlWrapper, useIsAccessControlActive } from '../modules/access-control';
import { AppFooter, AppTop } from './core';
import LayerControl from './Others/layerControl/LayerControl';
import FirebasePushNotifications from '../FirebasePushNotifications';
import { emptyNull } from '../shared/misc';
import vmFunctions from '../global-utils/vmFunctions';

const Temp404 = (props) => {
  const notFoundPage = useShallowEqualSelector((state) => state?.appState?.settings?.notFoundPage);
  if (notFoundPage && notFoundPage.isActive && notFoundPage.components?.length) {
    // if exists, active and has componentsArray
    return (
      <PageRenderer
        {...props}
        title={notFoundPage.title}
        pageName={notFoundPage.title}
        components={notFoundPage.components}
      />
    );
  }
  return '!404! TEMP 404 Page not found';
};

const AppWrapper = (props) => {
  const { appContainer, vsxPage } = useTheme();
  const {
    isAuthLoading,
    currentAppUrl,
    testFlightPageInfo,
    testFlightComponentsBuilding,
    isAdminClientPreview,
    settings: {
      appName,
      appHeader,
      appHeadBanner,
      appSettings = emptyNull,
      pages,
      autoRedirect
    } = emptyNull,
    currentPage: { title: pageTitle } = emptyNull
  } = useShallowEqualSelector((state) => state.appState) ?? emptyNull;
  const { appTitle, disableDefaultLayoutRestrictions } = appSettings;
  const isAccessControlActive = useIsAccessControlActive();
  const navigate = useNavigate();
  const { pathname: currentPathname } = useLocation();
  const [lastPathname, setLastPathname] = useState(currentPathname);
  const ifScrollToTop = lastPathname !== currentPathname;

  // Export navigate from useNavigate to variable stored in vmFunctions.navigate
  // This gives possibility to redirect from within vmFunctions.
  useEffect(() => {
    setVmFunctionNavigate(navigate);
  }, [navigate]);

  useEffect(() => {
    if (testFlightPageInfo) {
      const gotoPage = `${currentAppUrl}/test-flight-page`;
      navigate(gotoPage);
    }
  }, [testFlightPageInfo]);

  useEffect(() => {
    document.title = pageTitle ? `${appTitle || appName} | ${pageTitle}` : appTitle || appName;
  }, [appTitle, appName, pageTitle]);

  useEffect(() => setLastPathname(currentPathname), [currentPathname]);
  useEffect(() => {
    const isLeftAppBefore = storageAndCookie.get('lastLocationBeforeLeavingApp');
    if (isLeftAppBefore) {
      navigate(isLeftAppBefore);
      storageAndCookie.del('lastLocationBeforeLeavingApp');
    }
  }, []);

  const dynamicRouting = pages
    .filter((el) => el?.route?.path) // in case of auto merge issue and page is not a real page
    .filter((el) => el.isPublished !== false || isAdminClientPreview) // MEV-9002 - if page is not published, we do not want to show it, unless we are in admin preview mode
    .map((page) => ({
      path: currentAppUrl + page.route.path,
      exact: page.route.exact,
      element:
        isAccessControlActive && !isAdminClientPreview ? (
          <AccessControlWrapper
            page={page}
            renderLoading={() => <Loading />}
            renderError={() => 'Loading access error!'}
            renderElement={(pageProps) => (
              <DynamicPageRenderer key={page.id} {...props} {...pageProps} />
            )}
            vmFunctions={vmFunctions}
            {...props}
          />
        ) : (
          <DynamicPageRenderer key={page.id} {...props} {...page} />
        )
    }));

  const autoRedirectPages = (() => {
    if (!autoRedirect || !autoRedirect?.isActive || !autoRedirect?.urls?.length) return [];

    return autoRedirect.urls
      .filter((links) => links.fromUrl !== links.toUrl)
      .map((relinkPage) => ({
        path: relinkPage.fromUrl,
        exact: true,
        element: <Redirect to={relinkPage.toUrl} replace />
      }));
  })();

  let headBannerRouting;
  if (appHeadBanner?.isActive) {
    headBannerRouting = useRoutes(
      appHeadBanner?.routes.map((headBanner) => {
        if (headBanner) {
          const { route, ...rest } = headBanner;
          return {
            path: currentAppUrl + route.path,
            exact: route.exact || true,
            element: <HeadBanner {...props} {...rest} />
          };
        }
        return { path: '#' };
      })
    );
  }

  const RoutingElement = useRoutes([
    ...autoRedirectPages,
    ...dynamicRouting,
    {
      path: `${currentAppUrl}/test-flight-page`,
      element: <TestFlightPage {...props} />
    },
    { path: `${currentAppUrl}/oca/:uid/:accessToken`, element: <OcaPage /> },
    { path: `${currentAppUrl}/logout`, element: <LogoutPage /> },
    { path: `${currentAppUrl}/maintenance`, element: <MaintenancePage /> },
    { path: '*', element: <Temp404 {...props} /> }
  ]);
  if (ifScrollToTop) window.scrollTo(0, 0);
  if (testFlightComponentsBuilding) {
    return (
      <>
        <Suspense fallback={<Loading />}>
          <ComponentsArrRenderer
            {...props}
            components={vmRefProcessorApp.resolve(testFlightComponentsBuilding)}
          />
        </Suspense>
      </>
    );
  }
  return (
    <div
      className={`vsx_page ${isAdminClientPreview ? 'admin-client-preview' : ''}`}
      style={vsxPage?.style}
    >
      {appHeader?.isActive && !isAdminClientPreview && <Navbar {...props} {...appHeader} />}
      {!isAdminClientPreview && <AppTop {...props} />}
      {appHeadBanner?.isActive && headBannerRouting}
      <div
        className={`vsx_container ${
          disableDefaultLayoutRestrictions ? '' : 'container-fluid appWrapper-wrapper'
        } ${appContainer?.wrapper?.variant || ''}`}
        style={appContainer?.wrapper?.style}
      >
        <div
          className={`${disableDefaultLayoutRestrictions ? '' : 'container jme-container p-0'} ${
            appContainer?.content?.variant || ''
          }`}
          style={appContainer?.content?.style}
        >
          <AnyErrorBoundary>{isAuthLoading ? <Loading /> : RoutingElement}</AnyErrorBoundary>
        </div>
      </div>
      <FirebasePushNotifications />
      <LayerControl />
      {!isAdminClientPreview && <AppFooter {...props} />}
    </div>
  );
};

export default AppWrapper;
